import { LitElement, css, html, nothing } from '@lit'
import styles from '@nova/styles'
import { formatPrice, updateCartQuantity, handleize } from '@nova/utils'

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
    },
    compareAtPrice: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item = {}
  }

  get formattedFinalPrice() {
    if (!this.item.final_price) return nothing
    return formatPrice(this.item.final_price * this.item.quantity)
  }

  get formattedCompareAtPrice() {
    if (parseInt(this.compareAtPrice) === 0) return nothing
    return formatPrice(this.compareAtPrice * this.item.quantity)
  }

  _renderProperties() {
    if (Array.isArray(this.item.properties)) {
      return this.item.properties.filter(property => property[0][0] !== '_')
        .map(
          (property) => html` <div class="cart-item__options">
            <span>${property[0]} ${property[1]}</span>
          </div>`,
        )
    } else if (Object.keys(this.item.properties).length) {
      return Object.keys(this.item.properties).filter(key => key[0] !== '_')
        .map(
          (key) => html` <div class="cart-item__options">
            <span>${key} ${this.item.properties[key]}</span>
          </div>`,
        )
    }
    return nothing
  }

  _renderOptions() {
    return this.item.options_with_values.map((option) => {
      if (handleize(option.value) !== "title") {
        return html` <div class="cart-item__options flex">
          <dt class="hidden">${option.name}:</dt>
          <dd>${option.value}</dd>
        </div>`
      } else {
        return html` <div class="cart-item__options flex">
          <dt>${option.name}:</dt>
          <dd>${option.value}</dd>
        </div>`
      }
    })
  }

  _renderSellingplan() {
    return html` <div class="cart-item__options cart-item__selling-plan flex">
        <span>${this.item.variant_title}</span>
        <span>${this.item.selling_plan_allocation.selling_plan.name}</span>
      </div>`
  }

  _renderQtySelector() {
    if (this.item?.properties?.['_hide_qty_selector']) {
      return html`<div></div>`
    } else {
      return html`
        <nova-quantity-input
          initial=${this.item.quantity}
          @changeQuantityStarted=${this._handleQuantityChange}
          class="cart-item__quantity-input"
        ></nova-quantity-input>`
    }
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
    this.dispatchEvent(new CustomEvent('nova-change'))
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false,
    )
    this.loading = false
    document.dispatchEvent(new CustomEvent('nova:changeQuantityFinished'), {
      bubbles: true,
    })
  }

  render() {
    return html`
      <div class="cart-item__inner">
        <div class="cart-item__media">
          <a href=${this.item.url}>
            ${
              this.item.image
                ? html`<img
                  class="cart-item__image cover"
                  src=${this.item.image}
                  alt=${this.item.product_title}
                  width="180px"
                />`
                : nothing
            }
          </a>
        </div>
        <div class="cart-item__details">
          <div class="cart-item__head">
            <div class="cart-item__heading">
              <a class="cart-item__title" href=${this.item.url}>
                ${this.item.product_title}
              </a>
              ${typeof this.item.selling_plan_allocation !== 'undefined' ? this._renderSellingplan()
                : this.item.options_with_values ? this._renderOptions()
                  : nothing
              }
              ${this._renderProperties()}
            </div>
            <div class="cart-item__prices">
              <div class="cart-item__price-wrapper">
                ${
                  parseInt(this.compareAtPrice) !== 0
                    ? html`
                      <dl class="cart-item__discounted-prices">
                        <dt class="visually-hidden">Sale Price</dt>
                        <dd>${this.formattedFinalPrice}</dd>
                        <dt class="visually-hidden">Regular Price</dt>
                        <dd>
                          <s class="cart-item__compare-at-price">
                            ${this.formattedCompareAtPrice}
                          </s>
                        </dd>
                      </dl>
                    `
                    : html`<span>${this.formattedFinalPrice} </span>`
                }
              </div>
            </div>
          </div>

          <div class="cart-item__action">
            ${this._renderQtySelector()}
            <sl-button
              variant="text"
              class="cart-item__remove"
              @click=${() => this.updateQuantity(0)}
            >
              Remove
            </sl-button>
          </div>
        </div>
      </div>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
        width: 100%;
        margin-bottom: 30px;
      }
      .hidden {
        display: none;
      }
      .cart-item__inner {
        display: flex;
        gap: 10px;
      }
      .cart-item__media {
        width: 167px;
        min-width: 167px;
      }
      .cart-item__media img {
        display: block;
        width: 100%;
        height: auto;
      }
      .cart-item__details {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        font-size: 12px;
        line-height: 140%;
      }
      .cart-item__head {
        display: flex;
        width: 100%;
        justify-content: space-between;
      }
      .cart-item__details a {
        color: var(--nova-color-text);
        text-decoration: none;
        text-transform: capitalize;
        font-size: 16px;
        letter-spacing: -0.32px;
      }
      .cart-item__options {
        text-transform: capitalize;
        font-size: 12px;
        letter-spacing: -0.24px;
        margin: 5px 0;
      }
      .cart-item__options dt {
        margin-right: 5px;
      }
      .cart-item__options dd {
        margin-left: 0px;
      }
      .cart-item__selling-plan {
        flex-direction: column;
        gap: 2px;
      }
      .cart-item__prices {
        text-align: end;
        font-size: 12px;
        letter-spacing: -0.24px;
      }
      .cart-item__price-wrapper .visually-hidden {
        visibility: hidden;
        display: none;
      }
      .cart-item__discounted-prices {
        margin: 0;
      }
      .cart-item__compare-at-price {
        color: var(--nova-color-disabled);
      }
      .cart-item__action {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .cart-item__remove {
        height: 17px;
      }
      .cart-item__remove::part(base) {
        color: var(--nova-color-text);
        text-decoration: underline;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: 1;
        text-underline-offset: 5px;
        letter-spacing: -0.24px;
        width: min-content;
      }
      .cart-item__remove::part(label) {
        padding: 0;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.28px;
        height: 17px;
      }
      .cart-item__quantity-input {
        display: flex;
        justify-content: end;
      }
      .cart-item__quantity-input::part(wrapper) {
        border-color: var(--nova-color-border);
        --nova-height: 30px;
      }
      .cart-item__quantity-input::part(decrease) {
        padding: 0 5px 0 10px;
      }
      .cart-item__quantity-input::part(input) {
        background: transparent;
        width: 30px;
      }
      .cart-item__quantity-input::part(increase) {
        padding: 0 10px 0 5px;
      }

      @media screen and (min-width: 750px) {
        .cart-item__media {
          width: 180px;
          min-width: 180px;
        }

        .cart-item__details a {
          font-size: 18px;
          letter-spacing: -0.36px;
        }

        .cart-item__prices {
          font-size: 14px;
          letter-spacing: -0.28px;
        }

        .cart-item__compare-at-price {
          font-size: 12px;
          letter-spacing: -0.24px;
        }

        .cart-item__quantity-input::part(wrapper) {
          --nova-height: 40px;
          padding: 0 5px;
        }

        .cart-item__quantity-input::part(input) {
          font-size: 14px;
        }

        .cart-item__remove::part(base) {
          font-size: 14px;
          letter-spacing: -0.28px;
        }
      }
    `,
  ]
}

customElements.define('nova-cart-item', NovaCartItem)
